import map from 'lodash-es/map'

import { EMPTY_OBJECT } from '../../../constants/common'
import { getFileValueSize } from '../../File'
import {
  TableFileListCell,
  TableRoleCell,
  TableSwitchCell,
  TableTextCell,
  TableUserCell,
  TextListCell,
  toTableCellMultiValue,
} from '../../Table'
import { UserMultiValueContentLinked } from '../../User'
import {
  hasDocFlowAdditionalEmails,
  toRecipientArchivedMultiValue,
} from '../DocFlow.utils.recipients'
import { DocFlowSendingFieldName } from '../DocFlowSending'
import {
  DocFlowTableCellBuilding,
  DocFlowTableCellFlat,
  DocFlowTableCellRecipientStatus,
} from '../DocFlowTableCell'

function toAdditionalEmailsCellValue(additionalEmails) {
  return map(additionalEmails, item => toTableCellMultiValue(item.email))
}

function getEpostTooltipText({ canSend, permissions, t }) {
  if (!canSend) {
    return t('DocFlow.EpostSwitchNotEnoughCreditsTooltipKey')
  }

  if (permissions.file_is_over_size) {
    return t('DocFlow.EpostSwitchFileIsOverSizeTooltipKey')
  }

  if (permissions.file_has_too_many_pages) {
    return t('DocFlow.EpostSwitchFileHasTooManyPageseTooltipKey')
  }
}

function getEpostTooltip({
  canSend,
  epostDisabled,
  permissions,
  phaseDisabled,
  t,
}) {
  return (
    !phaseDisabled &&
    epostDisabled &&
    getEpostTooltipText({ canSend, permissions, t })
  )
}

export function renderDocFlowSendingTableCell({
  item,
  name,
  permissions = EMPTY_OBJECT,
  phaseDisabled,
  preparedFiles,
  scrollRef,
  t,
  onClickFile,
  onToggleSaveToProfile,
  onToggleSendAdditionalEmail,
  onToggleSendEmail,
  onToggleSendEpost,
  ...restProps
}) {
  const { [name]: value } = item
  const recipientValue = item[DocFlowSendingFieldName.Recipient]?.[name]

  const additionalEmails = item[DocFlowSendingFieldName.AdditionalEmail]
  const hasAdditionalEmails = hasDocFlowAdditionalEmails(item)

  const epostDisabled =
    !permissions.can_send_epost ||
    item[DocFlowSendingFieldName.EpostPermissions].file_has_too_many_pages ||
    item[DocFlowSendingFieldName.EpostPermissions].file_is_over_size

  switch (name) {
    case DocFlowSendingFieldName.Building:
      return (
        <DocFlowTableCellBuilding
          {...restProps}
          scrollRef={scrollRef}
          value={value}
        />
      )

    case DocFlowSendingFieldName.File:
      return (
        <TableFileListCell
          {...restProps}
          listProps={{
            moreItemsProps: {
              onClickItem: onClickFile,
            },
            onClickItem: onClickFile,
          }}
          scrollRef={scrollRef}
          value={preparedFiles}
        />
      )

    case DocFlowSendingFieldName.Flat:
      return (
        <DocFlowTableCellFlat
          {...restProps}
          item={item}
          scrollRef={scrollRef}
          value={value}
        />
      )

    case DocFlowSendingFieldName.Role:
      return <TableRoleCell {...restProps} value={recipientValue} />

    case DocFlowSendingFieldName.Status:
      return (
        <DocFlowTableCellRecipientStatus
          {...restProps}
          value={recipientValue}
        />
      )

    case DocFlowSendingFieldName.Recipient:
      return (
        <TableUserCell
          {...restProps}
          Content={UserMultiValueContentLinked}
          value={toRecipientArchivedMultiValue(value)}
        />
      )

    case DocFlowSendingFieldName.PagesCount:
      return <TableTextCell {...restProps} value={value} />

    case DocFlowSendingFieldName.SaveToProfile:
      return (
        <TableSwitchCell
          {...restProps}
          disabled={phaseDisabled}
          label={t('DocumentStapling.SendingTableSaveProfileTextKey')}
          value={value}
          onChange={onToggleSaveToProfile}
        />
      )

    case DocFlowSendingFieldName.SendEmail:
      return (
        <TableSwitchCell
          {...restProps}
          disabled={phaseDisabled}
          label={t('DocumentStapling.SendingTableSendEmailTextKey')}
          tooltip={
            !item.recipient.email &&
            !phaseDisabled &&
            t('DocumentStapling.SendingTableNoEmailTooltipKey')
          }
          value={value}
          onChange={onToggleSendEmail}
        />
      )

    case DocFlowSendingFieldName.SendAdditionalEmail:
      return (
        <TableSwitchCell
          {...restProps}
          disabled={phaseDisabled || !hasAdditionalEmails}
          label={t('DocFlow.SendingTableSendAdditionalEmailTextKey')}
          tooltip={
            !hasAdditionalEmails &&
            !phaseDisabled &&
            t('DocFlow.SendingTableNoAdditionalEmailTooltipKey')
          }
          value={value}
          onChange={onToggleSendAdditionalEmail}
        />
      )

    case DocFlowSendingFieldName.AdditionalEmail:
      return (
        <TextListCell
          {...restProps}
          scrollRef={scrollRef}
          value={toAdditionalEmailsCellValue(additionalEmails)}
        />
      )

    case DocFlowSendingFieldName.SendEpost:
      return (
        <TableSwitchCell
          {...restProps}
          disabled={phaseDisabled || (epostDisabled && !value)}
          label={t('DocFlow.SendingTableSendEpostTextKey')}
          tooltip={getEpostTooltip({
            canSend: permissions.can_send_epost,
            epostDisabled,
            permissions: item[DocFlowSendingFieldName.EpostPermissions],
            phaseDisabled,
            t,
          })}
          value={value}
          onChange={onToggleSendEpost}
        />
      )

    case DocFlowSendingFieldName.Size:
      return (
        <TableTextCell
          {...restProps}
          value={getFileValueSize({ unit: 'KB', value })}
        />
      )

    case DocFlowSendingFieldName.PreferredCommunication:
      return <TableTextCell {...restProps} value={value.value} />

    case DocFlowSendingFieldName.Email:
      return <TableTextCell {...restProps} value={recipientValue} />

    default:
      break
  }
}
